import { ApiClient } from "#/shared/api/client";
import { HttpError } from "#/shared/api/http";
import { isPresent } from "#ce/utils/props";

const BASE_PATH = "/api/directory_pdf_exports";

const STUB_QUERY_PARAM = "pdf_export_stub";

// A one-page empty PDF so the stub's download link opens a real document.
const STUB_PDF_DATA_URL =
  "data:application/pdf;base64,JVBERi0xLjQKMSAwIG9iajw8L1R5cGUvQ2F0YWxvZy9QYWdlcyAyIDAgUj4+ZW5kb2JqCjIgMCBvYmo8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PmVuZG9iagozIDAgb2JqPDwvVHlwZS9QYWdlL1BhcmVudCAyIDAgUi9NZWRpYUJveFswIDAgNjEyIDc5Ml0+PmVuZG9iagp4cmVmCjAgNAp0cmFpbGVyPDwvU2l6ZSA0L1Jvb3QgMSAwIFI+PgolJUVPRgo=";

class DirectoryPdfExportApi extends ApiClient {
  constructor() {
    super({ parse: false });
  }

  async create({ searchParams, consent }) {
    return this.post(BASE_PATH, {
      search_params: searchParams,
      consent,
    });
  }

  async status(id) {
    return this.get(`${BASE_PATH}/${encodeURIComponent(id)}`);
  }

  async email(id, { emails, preparerName, consent, emailToken, idempotencyKey }) {
    return this.request("post", `${BASE_PATH}/${encodeURIComponent(id)}/email`, {
      data: {
        emails,
        preparer_name: preparerName,
        consent,
      },
      headers: {
        "X-Directory-Email-Token": emailToken,
        "Idempotency-Key": idempotencyKey,
      },
    });
  }
}

// Backendless lifecycles, one per stub scenario:
//   - statuses:     the poll-by-poll status sequence, then `outcome` from there on
//   - createError:  rejects the create call instead of running a lifecycle
//   - emailError:   rejects the email call; generation itself runs the happy path
const STUB_SCENARIOS = Object.freeze({
  happy: { statuses: ["pending", "processing", "processing"], outcome: "complete" },
  slow: { statuses: ["pending", "pending", ...Array(12).fill("processing")], outcome: "complete" },
  failed: { statuses: ["pending", "processing"], outcome: "failed" },
  "rate-limited": {
    createError: () => new HttpError("Too many requests", { status: 429 }),
  },
  "email-invalid": {
    statuses: ["pending"],
    outcome: "complete",
    emailError: () =>
      new HttpError("Unprocessable", {
        status: 422,
        data: { errors: { emails: "Please enter at least one valid email address." } },
      }),
  },
  "email-rate-limited": {
    statuses: ["pending"],
    outcome: "complete",
    emailError: () => new HttpError("Too many requests", { status: 429 }),
  },
  "email-failed": {
    statuses: ["pending"],
    outcome: "complete",
    emailError: () => new HttpError("Server error", { status: 500 }),
  },
});

// Walks a created export through the scenario's lifecycle across consecutive
// status polls, completing with a data-URL PDF.
function createStubApi(scenario) {
  const script = STUB_SCENARIOS[scenario] ?? STUB_SCENARIOS.happy;
  let polls = 0;

  return {
    async create() {
      polls = 0;
      if (script.createError) {
        throw script.createError();
      }
      return { id: "stub-export", status: "pending", email_token: "stub-email-token" };
    },

    async status(id) {
      const status = script.statuses[polls] ?? script.outcome;
      polls += 1;
      if (status === "complete") {
        return { id, status, download_url: STUB_PDF_DATA_URL };
      }
      return { id, status };
    },

    async email() {
      await new Promise((resolve) => setTimeout(resolve, 800));
      if (script.emailError) {
        throw script.emailError();
      }
      return null;
    },
  };
}

/**
 * Resolves the app's stub attributes to the factory's `stub` option: off, `true`
 * (bare `stub-api` attribute), or a scenario name. When no `stub-api` attribute is
 * present and `allow-stub-param` is set — the server renders it only in development —
 * the `pdf_export_stub` query param selects the scenario.
 *
 * @param {Object} options
 * @param {Boolean|String} [options.stubApi] - The `stub-api` attribute value.
 * @param {Boolean|String} [options.allowStubParam] - The `allow-stub-param` attribute value.
 * @param {String} [options.search] - The page query string the param is read from.
 * @returns {Boolean|String}
 */
export function resolveStubOption({ stubApi = false, allowStubParam = false, search = "" } = {}) {
  if (isPresent(stubApi)) {
    return typeof stubApi === "string" && stubApi !== "" ? stubApi : true;
  }
  if (isPresent(allowStubParam)) {
    return new URLSearchParams(search).get(STUB_QUERY_PARAM) || false;
  }
  return false;
}

/**
 * Data access for directory PDF exports: create an export and poll its status.
 *
 * @param {Object} [options]
 * @param {Boolean|String} [options.stub] - Simulate the export lifecycle in the browser instead of
 *   calling the backend. `true` runs the happy path; a `STUB_SCENARIOS` key forces that flow, an
 *   unknown name falls back to happy.
 * @returns {{
 *   create: (payload: { searchParams: string, consent: boolean }) => Promise<Object>,
 *   status: (id: string) => Promise<Object>,
 *   email: (id: string, payload: { emails: string[], preparerName: string, consent: boolean,
 *     emailToken: string, idempotencyKey: string }) => Promise<null>,
 * }}
 */
export function createDirectoryPdfExportApi({ stub = false } = {}) {
  if (stub) {
    return createStubApi(stub);
  }
  return new DirectoryPdfExportApi();
}
